/** Turn a site-relative or absolute image path into a full URL for sharing / fetch. */
export function toAbsoluteUrl(href: string): string {
  if (!href) return "";
  const t = href.trim();
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const path = t.startsWith("/") ? t : `/${t}`;
  return `${origin}${path}`;
}

/** First usable product image URL (absolute), for share + Open Graph style use. */
export function getProductShareImageUrl(product: {
  thumbImage?: string[];
  images?: string[];
  variation?: Array<{ colorImage?: string; image?: string }>;
}): string | null {
  const candidates: string[] = [];
  if (product.thumbImage?.length) candidates.push(...product.thumbImage);
  if (product.images?.length) candidates.push(...product.images);
  const v0 = product.variation?.[0];
  if (v0?.colorImage) candidates.push(v0.colorImage);
  if (v0?.image) candidates.push(v0.image);
  const first = candidates.find((u) => u && String(u).trim());
  return first ? toAbsoluteUrl(String(first)) : null;
}

export function stripHtml(html: string): string {
  if (!html) return "";
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || d.innerText || "").replace(/\s+/g, " ").trim();
}

export function getProductPageUrl(productId: string | number): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/product/?id=${encodeURIComponent(String(productId))}`;
}

async function tryFetchImageAsFile(imageUrl: string, titleForFilename: string): Promise<File | null> {
  try {
    const res = await fetch(imageUrl, { mode: "cors", credentials: "omit", cache: "no-store" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.size) return null;
    let mime = blob.type || "image/jpeg";
    if (!mime.startsWith("image/")) mime = "image/jpeg";
    const ext = mime.includes("png")
      ? "png"
      : mime.includes("webp")
        ? "webp"
        : mime.includes("gif")
          ? "gif"
          : "jpg";
    const safe = String(titleForFilename || "produit")
      .replace(/[^\w.-]+/g, "_")
      .slice(0, 48);
    return new File([blob], `${safe || "produit"}.${ext}`, { type: mime });
  } catch {
    return null;
  }
}

export async function shareProductPage(opts: {
  title: string;
  descriptionPlain: string;
  url: string;
  /** Absolute image URL; used as a file when the platform supports it, and in text otherwise */
  imageUrl?: string | null;
  onCopySuccess?: () => void;
}): Promise<void> {
  const { title, descriptionPlain, url, imageUrl, onCopySuccess } = opts;
  const imageLine = imageUrl ? `Photo : ${imageUrl}` : "";
  const body = [title, descriptionPlain, url, imageLine].filter(Boolean).join("\n\n");
  const textWithoutPhotoLine = [title, descriptionPlain, url].filter(Boolean).join("\n\n");

  const file =
    imageUrl && imageUrl.startsWith("http")
      ? await tryFetchImageAsFile(imageUrl, title)
      : null;

  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title,
          text: textWithoutPhotoLine,
          url,
          files: [file],
        });
        return;
      } catch (err: unknown) {
        const name =
          err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
        if (name === "AbortError") return;
      }
    }

    try {
      await navigator.share({
        title,
        text: body,
        url,
      });
      return;
    } catch (err: unknown) {
      const name =
        err && typeof err === "object" && "name" in err ? String((err as { name: string }).name) : "";
      if (name === "AbortError") return;
    }
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(body);
      onCopySuccess?.();
      return;
    }
  } catch {
    /* fall through */
  }

  window.open(
    `https://wa.me/?text=${encodeURIComponent(body)}`,
    "_blank",
    "noopener,noreferrer"
  );
}
